// DagGraph: d3-dag Sugiyama layout rendered on canvas.
//
// Layout pipeline:
//   1. d3-dag graphStratify → sugiyama (coordCenter, decrossTwoLayer)
//   2. Re-group by layer and wrap into sub-rows fitting container width
//   3. Animate position transitions with GSAP
//   4. rAF loop drives hover fade-in/out on top of that base layer

import gsap from "gsap";
import type { LiveViewHook, CiPushEventWire } from "../types";
import {
  buildAdjacency,
  iconColor,
  statusLabel,
  formatDurationMs,
  hashHue,
  attrPathLabel,
  type GraphData,
  type JobStatus,
} from "./helpers";
import {
  activeJobStatuses,
  uiTerminalJobStatuses,
  jobStatusGlyph,
  validJobStatus,
  validFlakeOutputType,
} from "../../../../.dox/ci";
import { colors, cssRgb } from "./theme";
import {
  computeLayout,
  computeHoverHighlight,
  layoutSignature,
  type LayoutResult,
} from "./dag-layout";

interface HoverState {
  alpha: number;
  targetAlpha: number;
  visualId: string | null;
  highlighted: Set<string> | null;
  raf: number;
}

interface DagHook extends LiveViewHook<CiPushEventWire> {
  _nodePositions?: Map<string, { x: number; y: number }>;
  _prevPositions?: Map<string, { x: number; y: number }>;
  _onMouseMove?: (e: MouseEvent) => void;
  _onMouseLeave?: () => void;
  _onClick?: (e: MouseEvent) => void;
  _onResizeIntent?: (e: Event) => void;
  _resizeObserver?: ResizeObserver;
  _lastData?: GraphData;
  _drawFrame?: () => void;
  _resizeTween?: gsap.core.Tween;
  _spinnerRaf?: number;
  _hover?: HoverState;
  _filterAlphas?: Map<string, number>;
  _filterRaf?: number;
  _savedScrollTop?: number;
  _selectedId?: string | null;
  _statusFilter?: JobStatus | null;
  _layoutSignature?: string;
  _layoutAvailableW?: number;
  _canvasWidth?: number;
  _canvasHeight?: number;
  _renderOffsetX?: number;
  _ignoreResizeObserverUntil?: number;
  _resizeIntentTimer?: number;
  _resizeEase?: string;
  _pendingCenterId?: string | null;
  _lastSelectedId?: string | null;
  _scrollTween?: gsap.core.Tween;
}

// Current rotation angle for spinner icons — reads performance.now() each call
// so the arc advances every rAF frame. Matches Tailwind's animate-spin cadence
// (1s linear, full rotation).
const spinnerAngle = (): number =>
  ((performance.now() / 1000) * Math.PI * 2) % (Math.PI * 2);

// Single source of truth lives in helpers.dox::activeJobStatuses.
const isAnimatedStatus = (s: JobStatus): boolean =>
  (activeJobStatuses as ReadonlySet<JobStatus>).has(s);

// d3-dag stratify returns MutGraph<StratDatum, undefined> when called
// with our input shape. The layout functions (sugiyama, zherebko) accept
// any MutGraph. We cast with `as unknown as MutGraph` at the layout call
// site since d3-dag's overloaded generics defeat ReturnType inference.

// Single source of truth lives in helpers.dox::uiTerminalJobStatuses
// — the UI-terminal set explicitly adds `failed` to the FSM-terminal
// {cached, succeeded, cancelled}, matching this hook's policy: a node
// painted "failed" stays painted that way regardless of whether the
// executor later picks it back up via auto-retry. Pulling the same
// set from dox means a future status addition (e.g. a `paused` UI-
// terminal) lands here automatically rather than requiring a parallel
// edit to this hand-rolled superset.
const TERMINAL_STATUSES: ReadonlySet<string> =
  uiTerminalJobStatuses as ReadonlySet<string>;

const NODE_W = 260;
const NODE_H = 56;
const NODE_GAP_Y = 60;
const MARGIN = 30;
const LAYOUT_DIMS = {
  nodeW: NODE_W,
  nodeH: NODE_H,
  nodeGapY: NODE_GAP_Y,
  margin: MARGIN,
} as const;

const getLayoutSignature = (availableW: number): string =>
  layoutSignature(availableW, LAYOUT_DIMS);

function resizeCanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
): CanvasRenderingContext2D | null {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.scale(dpr, dpr);
  return ctx;
}

export const DagGraph = {
  mounted(this: DagHook) {
    this._selectedId = this.el.dataset["selected"] ?? null;
    this._lastSelectedId = null;
    this._pendingCenterId = this._selectedId;
    // The status-filter dataset attribute is server-rendered from
    // an Elixir `~p"...status_filter=..."` query string the user
    // controls. Validate at the DOM boundary so a typo trips
    // `Invalid JobStatus` here instead of as a silent no-op filter
    // (the inner draw code would have matched on a string outside
    // the union and rendered every node).
    const rawStatusFilter = this.el.dataset["statusFilter"];
    this._statusFilter = rawStatusFilter
      ? validJobStatus(rawStatusFilter)
      : null;

    this.handleEvent("graphUpdate", (d: unknown) => {
      // Runtime-validate the wire shape via the paradox-emitted
      // `validJobStatus` / `validFlakeOutputType`. `as GraphData`
      // alone is an UNCHECKED cast — TS trusts you without verifying
      // the runtime payload, so a server pushing the wrong wire
      // (e.g. snake-case `"queued"` instead of the dox illuminate
      // form `"Queued"`) blows up several frames deep in
      // `jobStatusGlyph`'s `default: throw "unreachable"` with no
      // useful stack. Validating per node here trips at the boundary
      // with a clear `Invalid JobStatus \`xxx\`` message naming the
      // offending value.
      const data = validateGraphData(d);
      this._lastData = data;
      renderDag(this, data);
    });

    this.handleEvent("graphState", (payload: unknown) => {
      const state = payload as {
        selected_id?: string | null;
        status_filter?: JobStatus | null;
      };
      this._lastSelectedId = this._selectedId ?? null;
      this._selectedId = state.selected_id ?? null;
      this._statusFilter = state.status_filter ?? null;
      if (this._drawFrame) startFilterAnimation(this);
    });

    this._onResizeIntent = (event: Event) => {
      if (!this._lastData) return;
      const detail = (
        event as CustomEvent<{
          width?: number;
          duration?: number;
          ease?: string;
        }>
      ).detail;
      const containerW = detail.width;
      if (containerW == null) return;

      const duration = detail.duration ?? 0.5;
      this._resizeEase = detail.ease ?? "power2.inOut";
      this._ignoreResizeObserverUntil =
        performance.now() + duration * 1000 + 80;
      if (this._resizeIntentTimer) window.clearTimeout(this._resizeIntentTimer);

      renderDag(this, this._lastData, {
        containerW,
        transitionDuration: duration,
        transitionEase: this._resizeEase,
      });

      this._resizeIntentTimer = window.setTimeout(
        () => {
          delete this._ignoreResizeObserverUntil;
          delete this._resizeIntentTimer;
          if (this._lastData) {
            renderDag(this, this._lastData, { animatePositions: false });
          }
        },
        duration * 1000 + 100,
      );
    };
    this.el.addEventListener(
      "pipeline:dag-resize-intent",
      this._onResizeIntent,
    );

    this._resizeObserver = new ResizeObserver(() => {
      if (!this._lastData) return;
      if (
        this._ignoreResizeObserverUntil != null &&
        performance.now() < this._ignoreResizeObserverUntil
      ) {
        return;
      }
      const canvas = this.el.querySelector("canvas");
      if (!canvas) return;

      const containerW =
        this.el.clientWidth || canvas.parentElement?.clientWidth || 800;
      const availableW = containerW - 30 * 2;
      const nextSignature = getLayoutSignature(availableW);

      if (
        this._layoutSignature === nextSignature &&
        this._layoutAvailableW != null &&
        this._canvasHeight != null &&
        this._drawFrame
      ) {
        // Same layout — just resize canvas and redraw, no re-layout needed
        const ctx = resizeCanvas(canvas, containerW, this._canvasHeight);
        if (!ctx) return;
        this._canvasWidth = containerW;
        this._renderOffsetX = (availableW - this._layoutAvailableW) / 2;
        this._drawFrame();
        return;
      }

      // Layout boundary crossed — re-layout
      if (this._resizeTween) this._resizeTween.kill();
      renderDag(this, this._lastData);
    });
    this._resizeObserver.observe(this.el);
  },

  beforeUpdate(this: DagHook) {
    const scroller = getScrollContainer(this);
    if (scroller) this._savedScrollTop = scroller.scrollTop;
  },

  updated(this: DagHook) {
    // Only data-selected and data-status-filter live on the element now.
    // Repaint without re-layout so LiveView attribute patches never jank the canvas.
    const scroller = getScrollContainer(this);
    if (scroller && this._savedScrollTop != null) {
      scroller.scrollTop = this._savedScrollTop;
    }
    if (this._drawFrame) {
      startFilterAnimation(this);
    }
  },

  destroyed(this: DagHook) {
    const canvas = this.el.querySelector("canvas");
    if (canvas) {
      if (this._onMouseMove)
        canvas.removeEventListener("mousemove", this._onMouseMove);
      if (this._onMouseLeave)
        canvas.removeEventListener("mouseleave", this._onMouseLeave);
      if (this._onClick) canvas.removeEventListener("click", this._onClick);
    }
    if (this._onResizeIntent) {
      this.el.removeEventListener(
        "pipeline:dag-resize-intent",
        this._onResizeIntent,
      );
    }
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._resizeTween) this._resizeTween.kill();
    if (this._scrollTween) this._scrollTween.kill();
    if (this._spinnerRaf) cancelAnimationFrame(this._spinnerRaf);
    if (this._filterRaf) cancelAnimationFrame(this._filterRaf);
    if (this._resizeIntentTimer) window.clearTimeout(this._resizeIntentTimer);
  },
};

function getScrollContainer(hook: DagHook): HTMLElement | null {
  return hook.el.closest("#dag-scroll-container");
}

function targetScrollTopForNode(
  hook: DagHook,
  nodeId: string,
  positions: Map<string, { x: number; y: number }>,
  canvasHeight: number,
): number | null {
  const scroller = getScrollContainer(hook);
  if (!scroller) return null;

  const pos = positions.get(nodeId);
  if (!pos) return null;

  const maxScroll = Math.max(0, canvasHeight - scroller.clientHeight);
  return Math.max(0, Math.min(maxScroll, pos.y - scroller.clientHeight / 2));
}

function animateCenterOnSelectedNode(
  hook: DagHook,
  positions: Map<string, { x: number; y: number }>,
  canvasHeight: number,
  duration = 0.35,
  ease = "power2.out",
): void {
  const nodeId = hook._pendingCenterId ?? hook._selectedId;
  const scroller = getScrollContainer(hook);
  if (!nodeId || !scroller) return;

  const target = targetScrollTopForNode(hook, nodeId, positions, canvasHeight);
  if (target == null) return;

  if (Math.abs(scroller.scrollTop - target) < 2) {
    scroller.scrollTop = target;
    if (hook._pendingCenterId === nodeId) delete hook._pendingCenterId;
    return;
  }

  if (hook._scrollTween) hook._scrollTween.kill();
  hook._scrollTween = gsap.to(scroller, {
    scrollTop: target,
    duration,
    ease,
    overwrite: true,
    onComplete: () => {
      delete hook._scrollTween;
      if (hook._pendingCenterId === nodeId) delete hook._pendingCenterId;
    },
  });
}

// Animate filter alpha transitions without re-running full layout.
// Lerps per-node alphas toward targets over ~10 frames, then stops.
function startFilterAnimation(hook: DagHook): void {
  if (hook._filterRaf) return; // already running
  const tick = () => {
    if (hook._drawFrame) hook._drawFrame();
    // Check if all alphas have converged
    const alphas = hook._filterAlphas;
    if (!alphas) {
      hook._filterRaf = 0;
      return;
    }
    let settled = true;
    for (const [, a] of alphas) {
      if (a > 0.16 && a < 0.99) {
        settled = false;
        break;
      }
    }
    if (settled) {
      hook._filterRaf = 0;
      return;
    }
    hook._filterRaf = requestAnimationFrame(tick);
  };
  hook._filterRaf = requestAnimationFrame(tick);
}

// Nerd-font glyphs sourced from `helpers.dox::jobStatusGlyph` (paradox
// codegen) — single source of truth shared with the favicon SVG and
// the Phoenix icon registry.
//
// Alias for back-compat with existing callers — the active-statuses
// set is codegen'd from helpers.dox so adding/removing in-flight
// statuses is a one-line dox edit, not a multi-site TS hunt.
const ANIMATED_STATUSES: ReadonlySet<JobStatus> = activeJobStatuses;

// helpers.dox returns the Nerd-font codepoint as a hex string
// (e.g. "f021") rather than the literal char, because paradox's
// string-literal parser rejects non-ASCII bytes. Decode at the boundary.
const decodeGlyph = (hex: string): string =>
  String.fromCodePoint(parseInt(hex, 16));

function drawStatusIcon(
  ctx: CanvasRenderingContext2D,
  status: JobStatus,
  cx: number,
  cy: number,
  size: number,
): void {
  const glyph = decodeGlyph(jobStatusGlyph(status));
  ctx.save();
  ctx.fillStyle = iconColor(status);
  ctx.font = `${size}px "Symbols Nerd Font"`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  if (ANIMATED_STATUSES.has(status)) {
    ctx.translate(cx, cy);
    ctx.rotate(spinnerAngle());
    ctx.fillText(glyph, 0, 0);
  } else {
    ctx.fillText(glyph, cx, cy);
  }
  ctx.restore();
}

// Boundary validator for the `graph:update` push payload.
// Walks each node and runs the paradox-emitted `validX` checks on
// the union-typed fields. Any drift between the server-side dox
// illuminate form and what the wire carries throws here, naming
// the offending value, instead of trickling into deeper consumers
// that hit `default: throw "unreachable"`.
function validateGraphData(d: unknown): GraphData {
  const raw = d as GraphData;
  for (const node of raw.nodes) {
    validJobStatus(node.status);
    if (node.outputType != null) validFlakeOutputType(node.outputType);
  }
  return raw;
}

function renderDag(
  hook: DagHook,
  data: GraphData,
  options: {
    animatePositions?: boolean;
    containerW?: number;
    transitionDuration?: number;
    transitionEase?: string;
  } = {},
): void {
  const animatePositions = options.animatePositions ?? true;
  const canvas = hook.el.querySelector("canvas");
  if (!canvas) return;
  if (data.nodes.length === 0) return;

  const nodeMap = new Map(data.nodes.map((n) => [n.id, n]));

  const margin = MARGIN;
  const containerW =
    options.containerW ??
    (hook.el.clientWidth || canvas.parentElement?.clientWidth || 800);
  const availableW = containerW - margin * 2;
  const nodeW = NODE_W;

  let layout: LayoutResult;
  try {
    layout = computeLayout({
      nodes: data.nodes,
      edges: data.edges,
      availableW,
      dims: LAYOUT_DIMS,
    });
  } catch (e) {
    console.error("[DagGraph] layout failed:", e);
    return;
  }

  // edgeRoutes: same shape the canvas mutation pass expected. Sugiyama mode
  // had no routes (empty map), Zherebko populated polylines per edge.
  const edgeRoutes = new Map<string, number[][]>();
  for (const e of layout.edges) {
    if (e.points) {
      edgeRoutes.set(
        `${e.source}:${e.target}`,
        e.points.map(([x, y]) => [x, y]),
      );
    }
  }

  const canvasW = containerW;
  const canvasH = layout.height;

  // Preserve scroll position across canvas resize — changing the canvas
  // height alters the scrollable area and the browser may clamp scrollTop.
  const scroller = getScrollContainer(hook);
  const savedScroll = scroller?.scrollTop ?? 0;

  if (scroller) scroller.scrollTop = savedScroll;

  const previousCanvasWidth = hook._canvasWidth ?? canvasW;
  const previousCanvasHeight = hook._canvasHeight ?? canvasH;
  const animatedCanvasWidth = Math.max(canvasW, previousCanvasWidth);
  const animatedCanvasHeight = Math.max(canvasH, previousCanvasHeight);
  const initialCtx = resizeCanvas(
    canvas,
    animatedCanvasWidth,
    animatedCanvasHeight,
  );
  if (!initialCtx) return;
  let ctx: CanvasRenderingContext2D = initialCtx;

  hook._layoutSignature = getLayoutSignature(availableW);
  hook._layoutAvailableW = availableW;
  hook._canvasWidth = animatedCanvasWidth;
  hook._canvasHeight = animatedCanvasHeight;
  hook._renderOffsetX = 0;

  // Positions already include margin offset — `computeLayout` does the
  // tx/ty translation internally.
  const targetPositions = new Map<string, { x: number; y: number }>(
    [...layout.positions].map(([id, p]) => [id, { x: p.x, y: p.y }]),
  );

  const prevPositions = hook._nodePositions ?? hook._prevPositions;
  const currentPositions = new Map<string, { x: number; y: number }>();
  for (const [id, target] of targetPositions) {
    const prev = prevPositions?.get(id);
    currentPositions.set(
      id,
      prev ? { x: prev.x, y: prev.y } : { x: target.x, y: target.y },
    );
  }
  hook._nodePositions = currentPositions;

  const adj = buildAdjacency(data.edges);

  // Precompute hash hues for nodes with downstream dependencies
  const nodeHue = new Map<string, number>();
  for (const node of data.nodes) {
    const down = adj.downstream.get(node.id);
    if (down && down.size > 0) {
      nodeHue.set(node.id, hashHue(node.attrPath ?? node.label));
    }
  }

  // Hover state persists across re-renders so clicking a node (which
  // triggers a LiveView update → renderDag) doesn't kill the highlight.
  if (!hook._hover) {
    hook._hover = {
      alpha: 0,
      targetAlpha: 0,
      visualId: null,
      highlighted: null,
      raf: 0,
    };
  }
  const hover = hook._hover;

  const drawFrame = () => {
    const canvasWidth = hook._canvasWidth ?? canvasW;
    const canvasHeight = hook._canvasHeight ?? canvasH;
    const renderOffsetX = hook._renderOffsetX ?? 0;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Status filter + filter alpha map
    const statusFilter = hook._statusFilter ?? null;
    if (!hook._filterAlphas) hook._filterAlphas = new Map();
    const fa = hook._filterAlphas;

    // Edges (alpha lerps with hover + filter)
    const ha = hover.alpha;
    const hl = hover.highlighted;
    const defaultEdge = 0.35;
    for (const link of layout.edges) {
      const srcId = link.source;
      const tgtId = link.target;
      const sp = currentPositions.get(srcId);
      const tp = currentPositions.get(tgtId);
      if (!sp || !tp) continue;
      const sx = sp.x + renderOffsetX;
      const sy = sp.y;
      const ex = tp.x + renderOffsetX;
      const ey = tp.y;

      const inChain = hl && hl.has(srcId) && hl.has(tgtId);
      const outChain = hl && (!hl.has(srcId) || !hl.has(tgtId));
      const targetA = inChain ? 1 : outChain ? 0.12 : defaultEdge;
      const edgeA = defaultEdge + (targetA - defaultEdge) * ha;

      // Filter: dim edges where either endpoint is filtered out
      const srcNode = nodeMap.get(srcId);
      const tgtNode = nodeMap.get(tgtId);
      const edgeFilterMatch =
        !statusFilter ||
        (srcNode?.status === statusFilter && tgtNode?.status === statusFilter);
      const edgeFilterAlpha = edgeFilterMatch ? 1 : 0.15;

      const srcHue = nodeHue.get(srcId);
      const filteredEdgeA = edgeA * edgeFilterAlpha;
      const edgeColor =
        srcHue != null
          ? `hsla(${srcHue}, 70%, 60%, ${filteredEdgeA})`
          : `rgba(255, 255, 255, ${filteredEdgeA})`;

      const routeKey = `${srcId}:${tgtId}`;
      const route = edgeRoutes.get(routeKey);

      const edgeIncomplete =
        srcNode != null && !TERMINAL_STATUSES.has(srcNode.status);

      ctx.beginPath();
      if (edgeIncomplete) ctx.setLineDash([6, 4]);
      if (route && route.length > 1) {
        // Zherebko routed edge — clamp endpoints to actual node positions
        const pts = route.map(([rx, ry]) => ({
          x: rx! + margin + renderOffsetX,
          y: ry! + margin,
        }));
        pts[0] = { x: sx, y: sy + NODE_H / 2 };
        pts[pts.length - 1] = { x: ex, y: ey - NODE_H / 2 };
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let pi = 1; pi < pts.length; pi++) {
          const prev = pts[pi - 1]!;
          const cur = pts[pi]!;
          const mcy = (prev.y + cur.y) / 2;
          ctx.bezierCurveTo(prev.x, mcy, cur.x, mcy, cur.x, cur.y);
        }
      } else {
        // Sugiyama — simple bezier between source bottom and target top
        const sy1 = sy + NODE_H / 2;
        const ey1 = ey - NODE_H / 2;
        const cy1 = sy1 + (ey1 - sy1) * 0.4;
        const cy2 = sy1 + (ey1 - sy1) * 0.6;
        ctx.moveTo(sx, sy1);
        ctx.bezierCurveTo(sx, cy1, ex, cy2, ex, ey1);
      }
      ctx.strokeStyle = edgeColor;
      ctx.lineWidth = 2;
      ctx.stroke();
      if (edgeIncomplete) ctx.setLineDash([]);

      // Arrow at target
      const arrowSize = 4;
      ctx.beginPath();
      ctx.moveTo(ex, ey - NODE_H / 2);
      ctx.lineTo(ex - arrowSize, ey - NODE_H / 2 - arrowSize * 1.5);
      ctx.lineTo(ex + arrowSize, ey - NODE_H / 2 - arrowSize * 1.5);
      ctx.closePath();
      ctx.fillStyle = edgeColor;
      ctx.fill();
    }

    // Nodes — iterate all nodes (including orphans not in dag)
    for (const [nid, pos] of currentPositions) {
      const node = nodeMap.get(nid);
      if (!node) continue;

      const nx = pos.x + renderOffsetX - nodeW / 2;
      const ny = pos.y - NODE_H / 2;
      const nw = nodeW;
      const nh = NODE_H;

      // Filter alpha: lerp toward 1.0 (match/no filter) or 0.15 (filtered out)
      const filterTarget =
        !statusFilter || node.status === statusFilter ? 1 : 0.15;
      const curFilterAlpha = fa.get(nid) ?? 1;
      const newFilterAlpha =
        curFilterAlpha + (filterTarget - curFilterAlpha) * 0.15;
      fa.set(
        nid,
        Math.abs(newFilterAlpha - filterTarget) < 0.01
          ? filterTarget
          : newFilterAlpha,
      );

      const isDimmed = hl && !hl.has(nid);
      const hoverNodeAlpha = isDimmed ? 0.15 : 1;
      const hoverAlpha = 1 + (hoverNodeAlpha - 1) * ha;
      ctx.globalAlpha = hoverAlpha * fa.get(nid)!;

      const isSelected = hook._selectedId === nid;
      ctx.beginPath();
      ctx.rect(nx, ny, nw, nh);
      ctx.fillStyle = cssRgb(colors().bgDark);
      ctx.fill();
      if (isSelected) {
        ctx.fillStyle = cssRgb(colors().accent, 0.1);
        ctx.fill();
      }
      const nHue = nodeHue.get(nid);
      ctx.strokeStyle = isSelected
        ? cssRgb(colors().accent)
        : nHue != null
          ? `hsl(${nHue}, 70%, 60%)`
          : cssRgb(colors().border);
      ctx.lineWidth = isSelected ? 2 : nHue != null ? 2 : 1.5;
      ctx.stroke();

      // Progress bar — bottom border of node for building jobs
      const isBuilding =
        (node.status === "building" || node.status === "retrying") &&
        node.estimatedMs &&
        node.buildStartedAt;
      if (isBuilding) {
        const elapsed = Date.now() - node.buildStartedAt;
        const progress = Math.min(elapsed / node.estimatedMs, 1);
        const barH = 2;
        const barY = ny + nh - barH;
        // Track fill
        ctx.fillStyle = iconColor(node.status);
        ctx.fillRect(nx, barY, nw * progress, barH);
        // Remaining (dimmed)
        ctx.fillStyle = cssRgb(colors().border, 0.3);
        ctx.fillRect(nx + nw * progress, barY, nw * (1 - progress), barH);
      }

      const pad = 10;
      const iconSize = 14;
      const iconX = nx + pad + iconSize / 2;
      const iconY = ny + 18;
      const textX = nx + pad + iconSize + 6;

      drawStatusIcon(ctx, node.status, iconX, iconY, iconSize);

      // Row 1: name
      const label = attrPathLabel(node.attrPath, node.label);
      const maxChars = Math.floor((nodeW - pad * 2 - iconSize - 6) / 8.5);
      const truncated =
        label.length > maxChars ? label.slice(0, maxChars - 1) + "…" : label;
      ctx.fillStyle = cssRgb(colors().text);
      ctx.font = "14px 'DejaVu Mono', monospace";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(truncated, textX, ny + 18);

      // Row 2: status + duration/ETA + badges
      ctx.font = "12px 'DejaVu Mono', monospace";
      const sLabel = statusLabel(node.status);
      ctx.fillStyle = iconColor(node.status);
      ctx.fillText(sLabel, textX, ny + 36);

      if (isBuilding) {
        const elapsed = Date.now() - node.buildStartedAt;
        const remaining = Math.max(0, node.estimatedMs - elapsed);
        ctx.fillStyle = cssRgb(colors().textSub);
        ctx.textAlign = "right";
        ctx.fillText(
          remaining > 0
            ? `~${formatDurationMs(remaining)} left`
            : `+${formatDurationMs(elapsed - node.estimatedMs)}`,
          nx + nw - pad,
          ny + 36,
        );
      } else if (node.durationMs) {
        ctx.fillStyle = cssRgb(colors().textSub);
        ctx.textAlign = "right";
        ctx.fillText(formatDurationMs(node.durationMs), nx + nw - pad, ny + 36);
      }

      let badgeX = textX + ctx.measureText(sLabel).width + 6;
      ctx.textAlign = "left";
      // Suppress the "cached" badge when the status itself is "cached" —
      // the row-2 status label already says "cached" and we'd otherwise
      // print it twice on the same row.
      if (node.cacheHit && node.status !== "cached") {
        ctx.fillStyle = cssRgb(colors().accent, 0.7);
        ctx.fillText("cached", badgeX, ny + 36);
        badgeX += 46;
      }
      if (node.isFlaky) {
        ctx.fillStyle = cssRgb(colors().warning);
        ctx.fillText("flaky", badgeX, ny + 36);
      }

      ctx.globalAlpha = 1;
    }

    // Hover tooltip
    if (hover.visualId && hover.alpha > 0) {
      const node = nodeMap.get(hover.visualId);
      const pos = currentPositions.get(hover.visualId);
      if (node && pos) {
        ctx.globalAlpha = hover.alpha;
        const label = node.attrPath ?? node.label;
        const dur = node.durationMs
          ? ` (${formatDurationMs(node.durationMs)})`
          : "";
        const text = `${label}${dur} — ${node.status}`;
        ctx.font = "11px 'DejaVu Mono', monospace";
        ctx.fillStyle = cssRgb(colors().bg, 0.9);
        const tw = ctx.measureText(text).width + 12;
        const tooltipX = Math.min(
          pos.x + renderOffsetX - tw / 2,
          canvasWidth - tw - 4,
        );
        const tooltipY = pos.y - NODE_H / 2 - 22;
        ctx.beginPath();
        ctx.roundRect(tooltipX, tooltipY, tw, 18, 4);
        ctx.fill();
        ctx.fillStyle = cssRgb(colors().text);
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(text, tooltipX + 6, tooltipY + 9);
        ctx.globalAlpha = 1;
      }
    }
  };

  hook._drawFrame = drawFrame;

  const positionsChanged =
    prevPositions &&
    [...targetPositions].some(([id, t]) => {
      const p = prevPositions.get(id);
      return !p || p.x !== t.x || p.y !== t.y;
    });
  const dimensionsChanged =
    previousCanvasWidth !== canvasW || previousCanvasHeight !== canvasH;

  if (
    animatePositions &&
    prevPositions &&
    prevPositions.size > 0 &&
    (positionsChanged || dimensionsChanged)
  ) {
    if (hook._resizeTween) hook._resizeTween.kill();
    if (hook._scrollTween) {
      hook._scrollTween.kill();
      delete hook._scrollTween;
    }
    const startCanvasWidth = previousCanvasWidth;
    const startCanvasHeight = previousCanvasHeight;
    const startScrollMax = scroller
      ? Math.max(0, scroller.scrollHeight - scroller.clientHeight)
      : 0;
    const finalScrollMax = Math.max(
      0,
      startScrollMax + (canvasH - previousCanvasHeight),
    );
    const startScrollTop = Math.min(savedScroll, startScrollMax);
    const centeredFinalScrollTop =
      hook._pendingCenterId != null
        ? targetScrollTopForNode(
            hook,
            hook._pendingCenterId,
            targetPositions,
            canvasH,
          )
        : null;
    const finalScrollTop =
      centeredFinalScrollTop != null
        ? centeredFinalScrollTop
        : Math.min(startScrollTop, finalScrollMax);
    const proxy = { t: 0 };
    hook._resizeTween = gsap.fromTo(
      proxy,
      { t: 0 },
      {
        t: 1,
        duration: options.transitionDuration ?? 0.6,
        ease: options.transitionEase ?? hook._resizeEase ?? "power2.inOut",
        onUpdate: () => {
          for (const [id, target] of targetPositions) {
            const prev = prevPositions.get(id);
            const cur = currentPositions.get(id);
            if (cur && prev) {
              cur.x = prev.x + (target.x - prev.x) * proxy.t;
              cur.y = prev.y + (target.y - prev.y) * proxy.t;
            } else if (cur) {
              cur.x = target.x;
              cur.y = target.y;
            }
          }

          hook._canvasWidth =
            startCanvasWidth + (canvasW - startCanvasWidth) * proxy.t;
          hook._canvasHeight =
            startCanvasHeight + (canvasH - startCanvasHeight) * proxy.t;
          const resizedCtx = resizeCanvas(
            canvas,
            Math.round(hook._canvasWidth),
            Math.round(hook._canvasHeight),
          );
          if (resizedCtx) ctx = resizedCtx;

          drawFrame();

          if (scroller) {
            scroller.scrollTop =
              startScrollTop + (finalScrollTop - startScrollTop) * proxy.t;
          }
        },
        onComplete: () => {
          for (const [id, target] of targetPositions) {
            const cur = currentPositions.get(id);
            if (cur) {
              cur.x = target.x;
              cur.y = target.y;
            }
          }
          hook._canvasWidth = canvasW;
          hook._canvasHeight = canvasH;
          const resizedCtx = resizeCanvas(canvas, canvasW, canvasH);
          if (resizedCtx) ctx = resizedCtx;
          hook._prevPositions = new Map(targetPositions);
          delete hook._resizeTween;
          if (hook._pendingCenterId === hook._selectedId) {
            delete hook._pendingCenterId;
          }
          drawFrame();
        },
      },
    );
  } else {
    if (hook._resizeTween) {
      hook._resizeTween.kill();
      delete hook._resizeTween;
    }
    drawFrame();
    hook._prevPositions = new Map(targetPositions);
  }

  if (
    hook._selectedId &&
    hook._selectedId !== hook._lastSelectedId &&
    !hook._resizeTween
  ) {
    animateCenterOnSelectedNode(
      hook,
      currentPositions,
      hook._canvasHeight ?? canvasH,
      0.3,
      "power2.out",
    );
  }
  hook._lastSelectedId = hook._selectedId ?? null;

  // rAF loop: redraws each frame while any node is in an animated status
  // (building/evaluating/retrying), so their spinner icons rotate smoothly.
  // Mirrors Tailwind's `animate-spin` in the list view.
  if (hook._spinnerRaf) cancelAnimationFrame(hook._spinnerRaf);
  const hasAnimatedNode = data.nodes.some((n) => isAnimatedStatus(n.status));
  if (hasAnimatedNode) {
    const tickSpinner = () => {
      drawFrame();
      hook._spinnerRaf = requestAnimationFrame(tickSpinner);
    };
    hook._spinnerRaf = requestAnimationFrame(tickSpinner);
  } else {
    hook._spinnerRaf = 0;
  }

  // rAF loop for hover fade
  const tickHover = () => {
    const speed = hover.targetAlpha > hover.alpha ? 0.12 : 0.18;
    hover.alpha += (hover.targetAlpha - hover.alpha) * speed;
    if (Math.abs(hover.alpha - hover.targetAlpha) < 0.01) {
      hover.alpha = hover.targetAlpha;
      if (hover.targetAlpha === 0) {
        hover.visualId = null;
        hover.highlighted = null;
      }
    }
    drawFrame();
    if (hover.alpha !== hover.targetAlpha) {
      hover.raf = requestAnimationFrame(tickHover);
    } else {
      hover.raf = 0;
    }
  };

  const setHover = (id: string | null) => {
    if (id && id !== hover.visualId) {
      hover.visualId = id;
      hover.alpha = 0;
      // Pure compute lives in dag-layout — same set the test exercises.
      hover.highlighted = new Set(computeHoverHighlight(adj, id));
    }
    hover.targetAlpha = id ? 1 : 0;
    if (!hover.raf) hover.raf = requestAnimationFrame(tickHover);
  };

  // Hit-test: find node under cursor
  const hitTest = (mx: number, my: number): string | null => {
    const halfW = nodeW / 2;
    const halfH = NODE_H / 2;
    const renderOffsetX = hook._renderOffsetX ?? 0;
    for (const [id, pos] of currentPositions) {
      if (
        mx >= pos.x + renderOffsetX - halfW &&
        mx <= pos.x + renderOffsetX + halfW &&
        my >= pos.y - halfH &&
        my <= pos.y + halfH
      ) {
        return id;
      }
    }
    return null;
  };

  // Remove previous listeners before adding new ones (prevents leak on re-render)
  if (hook._onMouseMove)
    canvas.removeEventListener("mousemove", hook._onMouseMove);
  if (hook._onMouseLeave)
    canvas.removeEventListener("mouseleave", hook._onMouseLeave);
  if (hook._onClick) canvas.removeEventListener("click", hook._onClick);

  hook._onMouseMove = (e: MouseEvent) => {
    const rect = canvas.getBoundingClientRect();
    const hit = hitTest(e.clientX - rect.left, e.clientY - rect.top);
    canvas.style.cursor = hit ? "pointer" : "default";
    setHover(hit);
  };

  hook._onMouseLeave = () => {
    setHover(null);
    canvas.style.cursor = "default";
  };

  hook._onClick = (e: MouseEvent) => {
    const rect = canvas.getBoundingClientRect();
    const hit = hitTest(e.clientX - rect.left, e.clientY - rect.top);
    const scroller = getScrollContainer(hook);
    if (scroller) hook._savedScrollTop = scroller.scrollTop;
    if (hit) {
      hook._pendingCenterId = hit;
      hook.pushEvent("jobSelect", { id: hit });
    } else {
      delete hook._pendingCenterId;
      hook.pushEvent("jobDeselect", {});
    }
  };

  canvas.addEventListener("mousemove", hook._onMouseMove);
  canvas.addEventListener("mouseleave", hook._onMouseLeave);
  canvas.addEventListener("click", hook._onClick);
}
